import {
	ExternalServiceErrorApiDto,
	RevolutErrorApiDto,
	XeroAuthErrorApiDto,
	XeroElementApiDto,
	XeroValidationErrorApiDto,
	XeroValidationErrorMessageApiDto
} from "../../api/model/rest";

export class ExternalServiceError {
	constructor(public readonly message: string,
		public readonly cause: RevolutError | XeroAuthError | XeroValidationError,
		public readonly type: ExternalServiceErrorType) {}

	static fromJson(json: ExternalServiceErrorApiDto, type: string): ExternalServiceError {
		let cause;
		switch (type) {
			case ExternalServiceErrorType.XeroError:
				if (json.cause.Elements) {
					cause = XeroValidationError.fromJson(json.cause as XeroValidationErrorApiDto)
				}
				else if (json.cause.error) {
					cause = XeroAuthError.fromJson(json.cause as XeroAuthErrorApiDto);
				}
				return new ExternalServiceError(json.message, cause, ExternalServiceErrorType.XeroError);
			case ExternalServiceErrorType.RevolutError:
				cause = RevolutError.fromJson(json.cause as RevolutErrorApiDto);
				return new ExternalServiceError(json.message, cause, ExternalServiceErrorType.RevolutError);
		}
		return new ExternalServiceError(json.message, null, ExternalServiceErrorType.UnknownError);
	}

	/**
	 * If specific message is unavailable, returns more general one.
	 */
	getErrorMessage() {
		const message = this.cause && this.cause.getErrorMessage();
		return message && !!message.length ? message : this.message;
	}
}

export class RevolutError {
	constructor(public readonly message: string, public readonly code: string) {}

	static fromJson(json: RevolutErrorApiDto) {
		return new RevolutError(json.message, json.code);
	}

	/**
	 * Returns readable message from revolut.
	 */
	getErrorMessage() {
		return this.message;
	}
}

export class XeroAuthError {
	constructor(public readonly error: string) {}

	static fromJson(json: XeroAuthErrorApiDto) {
		return new XeroAuthError(json.error)
	}

	/**
	 *  Returns OAuth 2.0 error message in readable form eg. invalid_grant -> Invalid grant
	 *  https://developer.xero.com/documentation/oauth2/troubleshooting
	 */
	getErrorMessage() {
		return this.error && !!this.error.length ? this.error.capitalize().replaceUnderscoresWithSpaces() : undefined;
	}
}

export class XeroValidationError {
	constructor(public readonly errorNumber: number,
		public readonly type: string,
		public readonly message: string,
		public readonly elements: XeroElement[]) {}

	static fromJson(json: XeroValidationErrorApiDto) {
		const elements = json.Elements ? json.Elements.map(XeroElement.fromJson) : [];
		return new XeroValidationError(json.ErrorNumber, json.Type, json.Message, elements);
	}

	/**
	 * Joins all validation error messages with dot. If no messages, returns error general message.
	 */
	getErrorMessage(): string {
		const message = this.elements.map(element => element.validationErrors.map(ve => ve.message).join(". ")).join("");
		return message && !!message.length ? message : this.message;
	}
}

class XeroElement {
	constructor(public readonly validationErrors: XeroValidationErrorMessage[]) {}

	static fromJson(json: XeroElementApiDto) {
		const validationErrors = json.ValidationErrors ? json.ValidationErrors.map(XeroValidationErrorMessage.fromJson) : [];
		return new XeroElement(validationErrors);
	}
}

class XeroValidationErrorMessage {
	constructor(public readonly message: string) {}

	static fromJson(json: XeroValidationErrorMessageApiDto) {
		return new XeroValidationErrorMessage(json.Message);
	}
}

export enum ExternalServiceErrorType {
	RevolutError = "RevolutBadRequestException",
	XeroError = "XeroBadRequestException",
	UnknownError = "UnknownError"
}
